import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import { useOrderDetailContext } from 'providers/OrderDetailProvider';

import { orderApi } from 'api';

import { TIME_OPTION } from 'constants/time';

const HISTORY_PAGE_SIZE = 25;

const OrderHistory = ({ orderId }) => {
  const { orderDetailData } = useOrderDetailContext();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);

  // Incremented per request so a response that arrives after orderId changed
  // (or after a newer request started) is dropped instead of merged.
  const requestSeqRef = useRef(0);

  const fetchOrderHistory = useCallback(
    async (pageToLoad, { append = false } = {}) => {
      const requestSeq = ++requestSeqRef.current;
      const isCurrent = () => requestSeqRef.current === requestSeq;

      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        const params = new URLSearchParams({
          page: String(pageToLoad),
          page_size: String(HISTORY_PAGE_SIZE),
        });
        const response = await orderApi.getOrderHistory(orderId, params);

        if (!isCurrent()) return;

        // Bare array = server not paginated yet; keep backward compatibility.
        const results = Array.isArray(response)
          ? response
          : response?.results || [];
        const next = Array.isArray(response) ? null : response?.next;

        setHistoryData(prev => {
          if (!append) return results;
          // A log written between page loads shifts the pages; skip rows we
          // already have so keys stay unique.
          const seen = new Set(prev.map(row => row.id));
          return [...prev, ...results.filter(row => !seen.has(row.id))];
        });
        setHasNext(Boolean(next));
        setPage(pageToLoad);
      } catch (error) {
        if (!isCurrent()) return;
        toast.error('Failed to fetch order history');
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [orderId],
  );

  useEffect(() => {
    if (orderId) {
      setHistoryData([]);
      setPage(1);
      setHasNext(false);
      fetchOrderHistory(1);
    }
    return () => {
      // Invalidate in-flight requests for the previous orderId / on unmount.
      requestSeqRef.current += 1;
    };
  }, [orderId, fetchOrderHistory]);

  const handleLoadMore = () => {
    fetchOrderHistory(page + 1, { append: true });
  };

  const parseChanges = changesStr => {
    try {
      return JSON.parse(changesStr);
    } catch (error) {
      return changesStr;
    }
  };

  const renderChanges = changes => {
    if (typeof changes === 'string' && !changes.startsWith('{')) {
      return <p className="fs-10 mb-0">{changes}</p>;
    }

    const changesObj =
      typeof changes === 'string' ? parseChanges(changes) : changes;

    if (typeof changesObj !== 'object' || changesObj === null) {
      return <p className="fs-10 mb-0">{String(changes)}</p>;
    }

    return Object.entries(changesObj)
      .filter(([key, value]) => Array.isArray(value) && value.length === 2)
      .map(([key, value]) => (
        <p key={key} className="fs-10 mb-1">
          <span className="fw-medium">{key}:</span> {value[0]} =&gt; {value[1]}
        </p>
      ));
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <h5 className="fs-9 mb-0 text-nowrap py-2 py-xl-0">
          {`Order #${orderDetailData?.channelOrderId} Activities`}
        </h5>
      </Card.Header>

      <Card.Body className="p-3">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-3">
            Loading...
          </div>
        ) : historyData.length === 0 ? (
          <div className="text-center py-3">
            No history exist for this order
          </div>
        ) : (
          <div className="timeline-vertical">
            {historyData.map((history, index) => {
              const { id, model_name, target_id, actor, changes, timestamp } =
                history;

              return (
                <div
                  key={id}
                  className={classNames('timeline-item', {
                    'timeline-item-start': index % 2 === 0,
                    'timeline-item-end': index % 2 !== 0,
                  })}
                >
                  <div className="timeline-icon icon-item icon-item-lg text-primary border-300" />

                  <Row
                    className={` ${
                      index % 2 == 0
                        ? 'timeline-item-start'
                        : 'timeline-item-end'
                    }`}
                  >
                    <Col lg={6} className="timeline-item-time">
                      <div>
                        <p className="fs-10 text-600 mb-1">
                          {new Date(timestamp).toLocaleString(
                            'en-IN',
                            TIME_OPTION,
                          )}
                        </p>

                        {actor ? (
                          <span className="fs-10 text-600">
                            {actor?.email || '-'}
                          </span>
                        ) : null}
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="timeline-item-content">
                        <div className="timeline-item-card">
                          <h6 className="mb-2">{`${model_name} ${target_id}`}</h6>

                          {renderChanges(changes)}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              );
            })}
          </div>
        )}

        {!loading && hasNext && (
          <div className="d-flex justify-content-center mt-3">
            <Button
              variant="falcon-default"
              size="sm"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : 'Load more'}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrderHistory;
